import React, { useContext, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom/cjs/react-router-dom";
import { useDispatch } from "react-redux";
import ProjectHeader from "../../_shared/ProjectHeader/ProjectHeader";
import { HeaderContext } from "../../../../HeaderContext";
import { breadCrumbsArray } from "../../../../data/breadCrumbs";
import { setDynamicBreadcrumbs } from "../../_shared/_redux/ProjectActions";
import {
  EXECUTION_BLUEPRINT,
  EXECUTION_BLUEPRINT_LOCKED_VIEW,
} from "../../../../data/RouteUrls";
import useExecutionBlueprintStore, {
  useExecutionBlueprintStoreSelector,
} from "../_redux/ExecutionBlueprintStore";
import LockedBlueprintView, {
  LockedBlueprintViewSkeleton,
  isLockedTab,
  LOCKED_TABS,
} from "../ExecutionBlueprintLiveReview/_shared/LockedBlueprintView";
import WigConnectionsCanvas from "../ExecutionBlueprintWigConnections/WigConnectionsCanvas";
import "../ExecutionBlueprintLiveReview/_shared/LockedBlueprintView.scss";

// ─────────────────────────────────────────────────────────────────────────────
// The locked, read-only view — the coach's wrapper. Figma node 33869:43260.
//
// One screen, three tabs (Blueprint / WIG Connections / Year 1 Plan), and the
// ONLY locked screen — the hub that used to sit in front of it was deleted on
// 2026-09-02. The tab is a ROUTE PARAM, not local state: the design's breadcrumb
// reads "Execution Blueprint › WIG Connections", so the tab is part of where you
// are — and a locked blueprint is the sort of thing one person links another to.
//
// The canvas is INJECTED into the shared view rather than imported by it,
// because it sits outside `_shared/` at a different relative path in each app —
// importing it there is the one thing that would break the two copies' identity.
//
// ⚠️ REST, NOT THE LIVE SOCKET. An earlier version of the deleted hub joined
// the `/live-review` socket and read its snapshot; that was removed on
// instruction, and the instruction was right — the live session ENDS at the
// terminal lock, so a screen someone can open days or weeks later cannot depend
// on being in a room that closed. This screen reads the record over REST.
// All three tabs share ONE fetch: they are three views of one immutable record,
// and the store caches it per blueprint, so switching tabs costs nothing.
// ─────────────────────────────────────────────────────────────────────────────

// Re-read for the PDF only: the export prints the company in the document
// header and in the filename, which is what makes a downloaded blueprint
// identifiable once it is sitting in someone's Downloads folder.
const selectLocked = (state) => state.lockedByUniqueId;
const selectLockedLoading = (state) => state.lockedLoading;
const selectLockedError = (state) => state.lockedError;
const selectLockedDownloading = (state) => state.lockedDownloading;
const selectLockedDownloadError = (state) => state.lockedDownloadError;

const TAB_LABEL = {
  [LOCKED_TABS.BLUEPRINT]: "Blueprint",
  [LOCKED_TABS.CONNECTIONS]: "Execution Blueprint Diagram with Connectors",
  [LOCKED_TABS.YEAR_ONE]: "Prioritised WIGs for the next 12 months",
};

const LockedBlueprintScreen = () => {
  const history = useHistory();
  const { blueprintId, tab } = useParams();
  const dispatch = useDispatch();

  const lockedByUniqueId = useExecutionBlueprintStoreSelector(selectLocked);
  const loading = useExecutionBlueprintStoreSelector(selectLockedLoading);
  const error = useExecutionBlueprintStoreSelector(selectLockedError);
  const downloading = useExecutionBlueprintStoreSelector(
    selectLockedDownloading,
  );
  const downloadError = useExecutionBlueprintStoreSelector(
    selectLockedDownloadError,
  );
  const blueprint = lockedByUniqueId?.[blueprintId] || null;
  // ⚠️ `clients` / `companyName` were removed here on 2026-09-03. The only thing
  // on this screen that wanted the company's name was the client-side PDF — it
  // went in the document's header band and its filename. The server generates the
  // document now and names its own file, and nothing else on the screen displays
  // the company, so both the lookup and the dashboard read went with it.

  // An unrecognised tab in the URL falls back to the first rather than
  // rendering nothing — a hand-typed or stale link should still land somewhere.
  const activeTab = isLockedTab(tab) ? tab : LOCKED_TABS.BLUEPRINT;

  const HeaderMetaData = useContext(HeaderContext);
  useEffect(() => {
    document.title = `Art of Scale | ${TAB_LABEL[activeTab]}`;
    HeaderMetaData.setHeaderMetadata({ headerTitle: "Execution Blueprint" });
    // eslint-disable-next-line
  }, [activeTab]);

  // "Execution Blueprint › ‹tab›" — two crumbs, on instruction (2026-09-02),
  // which is also D41's shape.
  //
  // ⚠️ The MIDDLE "Locked Blueprint" crumb is GONE, and had to be: it linked to
  // the hub, and the hub no longer exists. A crumb pointing at a deleted route
  // is worse than no crumb, and leaving it as inert text would have named a
  // screen nobody can reach.
  //
  // No "Blueprint Review" (company) crumb either, which predates this and still
  // holds — the review screen belongs to the pre-lock flow, and nothing above
  // this one survives the terminal lock. The static trail matches.
  useEffect(() => {
    dispatch(
      setDynamicBreadcrumbs(EXECUTION_BLUEPRINT_LOCKED_VIEW, [
        breadCrumbsArray[EXECUTION_BLUEPRINT][0],
        { title: TAB_LABEL[activeTab], url: "" },
      ]),
    );
  }, [dispatch, blueprintId, activeTab]);

  // One fetch for all three tabs — they are three views of one immutable
  // record. This screen is now the first thing to load it (the hub used to
  // fetch ahead of it), so it is genuinely self-sufficient; the store still
  // caches per blueprint, so switching tabs costs nothing.
  useEffect(() => {
    useExecutionBlueprintStore.getState().fetchLockedBlueprint(blueprintId);
  }, [blueprintId]);

  // The header's back arrow returns to the coach's client dashboard. It used to
  // return to the hub, which is where you came from — with the hub deleted the
  // dashboard is the only place above this screen, and it is what the deleted
  // hub's own back arrow pointed at. The view itself still has no back button of
  // its own (removed on instruction).
  const goToDashboard = () => history.push(EXECUTION_BLUEPRINT);

  return (
    <div>
      <ProjectHeader handleNavigation={goToDashboard} showBack />

      <div id="execution-blueprint-locked-view">
        {error?.code === "endpoints_not_wired" ? (
          <div className="lbv-empty">
            The locked blueprint is served by an API that isn&apos;t wired up
            yet. Nothing has gone wrong — this screen will fill in as soon as
            the endpoints are confirmed.
          </div>
        ) : error ? (
          <div className="lbv-empty" role="alert">
            {error.message}
          </div>
        ) : !blueprint && loading ? (
          // A skeleton of the view, not a line of text (2026-08-31, both apps).
          // `aria-busy` carries what the words used to say, without taking up
          // space in the layout or moving everything when it disappears.
          <div aria-busy="true">
            <LockedBlueprintViewSkeleton />
          </div>
        ) : (
          // ⚠️ The download is the SCREEN's, not the shared view's (2026-09-03).
          // The endpoint differs per app — the coach's sits behind `/coach/` — and
          // `LockedBlueprintView` is byte-identical across both repos, so it cannot
          // own the request. It owns the button; this owns the call.
          <LockedBlueprintView
            blueprint={blueprint}
            tab={activeTab}
            onDownload={() =>
              useExecutionBlueprintStore
                .getState()
                .downloadLockedBlueprint(blueprintId)
            }
            downloading={downloading}
            downloadError={downloadError}
            onTab={(next) =>
              history.replace(
                EXECUTION_BLUEPRINT_LOCKED_VIEW.replace(
                  ":blueprintId",
                  blueprintId,
                ).replace(":tab", next),
              )
            }
            CanvasComponent={WigConnectionsCanvas}
          />
        )}
      </div>
    </div>
  );
};

export default LockedBlueprintScreen;
